import { getService } from '@databases/service-container';
import { useCallback, useSyncExternalStore } from 'react';

import { databasesPingRegistry } from './ping-registry.ts';

/**
 * Returns whether one row owns the active selection handle and refreshes only
 * when that row's scoped selection invalidation fires.
 */
export function useRowSelectionHandleActive(entryId: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const pingKey = databasesPingRegistry.key('row', { entryId });

      return databasesPingRegistry.subscribeTo(
        pingKey,
        'selectionChanged',
        onStoreChange
      );
    },
    [entryId]
  );

  const getSnapshot = useCallback(() => {
    const selectionsApi = getService('SelectionsApi');

    if (selectionsApi.getSelectionMode() !== 'row') {
      return false;
    }

    const focusedCell = selectionsApi.getFocusedCell();

    if (!focusedCell) {
      return false;
    }

    const focusedEntryId = selectionsApi.resolvePositionToIds(focusedCell, 'row').entryId;

    if (
      focusedEntryId !== entryId ||
      (!selectionsApi.canMoveUp() && !selectionsApi.canMoveDown())
    ) {
      return false;
    }

    return selectionsApi.isRowSelected(focusedCell.row.index);
  }, [entryId]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
